// The import review's skip question (Wrangler v1.4.0). Alchemer's "is not one of" imports as plain
// not_in ("answered, and not one of"), which leaves out people who skipped the question. Where the
// question isn't required in Alchemer (or Alchemer doesn't say), the import result lists the quota
// in skippable_not_in, and the reviewer is asked whether to count skippers too: (not_in OR
// not_answered) on the same question.

const isLeaf = (node) => Boolean(node) && 'question_id' in node;
const isGroup = (node) => Boolean(node) && Array.isArray(node.children);

// A not_in that already sits in an OR group with not_answered on its question counts skippers.
const coversBlanks = (group, questionId) => group?.op === 'or'
  && group.children.some((c) => isLeaf(c) && c.operator === 'not_answered' && c.question_id === questionId);

/** The flagged questions that still have a plain not_in in this logic, so still leave skippers out. */
export const uncountedSkips = (logic, questionIds) => {
  const flagged = new Set(questionIds);
  const found = new Set();
  const walk = (node, parent) => {
    if (isLeaf(node)) {
      if (node.operator === 'not_in' && flagged.has(node.question_id) && !coversBlanks(parent, node.question_id)) {
        found.add(node.question_id);
      }
    } else if (isGroup(node)) {
      node.children.forEach((child) => walk(child, node));
    }
  };
  walk(logic, null);
  return questionIds.filter((id) => found.has(id));
};

/** The logic with each plain not_in on a flagged question changed to (not_in OR not_answered). */
export const countingSkips = (logic, questionIds) => {
  const flagged = new Set(questionIds);
  const change = (node, parent) => {
    if (isLeaf(node)) {
      if (node.operator !== 'not_in' || !flagged.has(node.question_id) || coversBlanks(parent, node.question_id)) return node;
      return { op: 'or', children: [node, { question_id: node.question_id, operator: 'not_answered' }] };
    }
    if (isGroup(node)) return { ...node, children: node.children.map((child) => change(child, node)) };
    return node;
  };
  return change(logic, null);
};

/** The import-time counts with skippers, by form: {ours, withSkips} or {and, andWithSkips, or, orWithSkips}. */
export const skipCounts = (check) => {
  if (!check) return null;
  if ('and' in check) {
    if (check.and_with_skips == null && check.or_with_skips == null) return null;
    return { alchemer: check.alchemer, and: check.and, andWithSkips: check.and_with_skips, or: check.or, orWithSkips: check.or_with_skips };
  }
  if (check.ours_with_skips == null) return null;
  return { alchemer: check.alchemer, ours: check.ours, withSkips: check.ours_with_skips };
};

// "No" is kept in this browser, per import job, like the not-converted banner's Dismiss. "Yes" needs
// no record: the quota's logic then counts skippers, which uncountedSkips sees.
const answeredKey = (surveyId, jobId, quotaId) => `wrangler-skips-kept-${surveyId}-${jobId}-${quotaId}`;
export const readKept = (surveyId, jobId, quotaId) => {
  try { return window.localStorage.getItem(answeredKey(surveyId, jobId, quotaId)) === '1'; } catch { return false; }
};
export const writeKept = (surveyId, jobId, quotaId) => {
  try { window.localStorage.setItem(answeredKey(surveyId, jobId, quotaId), '1'); } catch { /* not kept */ }
};
